import { useEffect, useMemo, useState } from 'react';
import { addVariant, updateVariant } from '../../lib/labData';
import { formatPct, resolveVariant } from '../../lib/lab';

const blankOverride = () => ({ ingredient_id: '', pct: '' });

/* A variant is "the same formula, with this one line different".
 *
 * Only the difference is stored. The rest resolves from the version at batch
 * time, so improving the base improves every variant — which is exactly what
 * a generic "Lip Balm" formula with a fixed 1% flavour oil could not express.
 */
export default function VariantForm({ formula, version, variant, onClose, onSaved }) {
  const editing = Boolean(variant?.id);
  const [name, setName] = useState(variant?.name || '');
  const [status, setStatus] = useState(variant?.status || 'active');
  const [notes, setNotes] = useState(variant?.notes || '');
  const [overrides, setOverrides] = useState(() =>
    variant?.overrides?.length
      ? variant.overrides.map((o) => ({ ingredient_id: o.ingredient_id, pct: o.pct }))
      : [blankOverride()]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const lines = useMemo(() => version?.formula_ingredients || [], [version]);
  // Only library-linked lines can be overridden: an override is keyed by
  // ingredient id, and a line typed as free text has none.
  const overridable = lines.filter((l) => l.ingredient_id);
  const unlinked = lines.length - overridable.length;
  const ingredients = lines.map((l) => l.ingredient).filter(Boolean);

  // Resolved live, so the flex line's new percentage — and any breach of a
  // cap — is visible while typing rather than at save time.
  const preview = useMemo(
    () =>
      resolveVariant({
        lines,
        variant: {
          name: name || 'This variant',
          overrides: overrides
            .filter((o) => o.ingredient_id && o.pct !== '')
            .map((o) => ({ ingredient_id: o.ingredient_id, pct: Number(o.pct) })),
        },
        flexIngredientId: version?.flex_ingredient_id,
        ingredients,
      }),
    [lines, name, overrides, version, ingredients]
  );

  // Guarded: without a flex ingredient these would match the first line
  // whose ingredient_id is also null.
  const flexId = version?.flex_ingredient_id || null;
  const flexLine = flexId ? lines.find((l) => l.ingredient_id === flexId) : null;
  const resolvedFlex = flexId ? preview.lines.find((l) => l.ingredient_id === flexId) : null;

  const updateOverride = (i, field, value) =>
    setOverrides((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give the variant a name.');
      return;
    }
    const clean = overrides
      .filter((o) => o.ingredient_id && o.pct !== '')
      .map((o) => ({ ingredient_id: o.ingredient_id, pct: Number(o.pct) }));
    if (!clean.length) {
      setError('A variant needs at least one line that differs from the base.');
      return;
    }
    if (preview.error) {
      setError(preview.error.message);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fields = {
        formula_id: formula.id,
        name: name.trim(),
        overrides: clean,
        status,
        notes: notes.trim() || null,
      };
      if (editing) await updateVariant(variant.id, fields);
      else await addVariant(fields);
      await onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="asset-modal-backdrop" onClick={onClose}>
      <div
        className="version-modal"
        role="dialog"
        aria-modal="true"
        aria-label={editing ? `Edit ${variant.name}` : 'New variant'}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="asset-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <header className="version-modal-head">
          <div>
            <h3>{editing ? 'Edit variant' : 'New variant'}</h3>
            <span className="formula-detail-type">
              {formula.name} · v{version?.version_number}
            </span>
          </div>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="lab-form-grid">
            <label className="lab-form-wide">
              Variant name
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Hazelnut Latte"
                required
              />
            </label>
            <label>
              Status
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="active">Active</option>
                <option value="retired">Retired</option>
              </select>
            </label>
          </div>

          <h4 className="batch-section-heading">What differs</h4>
          {!version?.flex_ingredient_id && (
            <p className="formula-warning">
              This version has no flex ingredient, so there's nothing to absorb the difference.
              Nominate one in the formula editor first.
            </p>
          )}
          {unlinked > 0 && (
            <p className="batch-form-hint">
              {unlinked} line{unlinked === 1 ? '' : 's'} can't be overridden yet because
              {unlinked === 1 ? " it isn't" : " they aren't"} linked to the ingredient library.
            </p>
          )}

          <div className="temp-rows">
            {overrides.map((override, i) => (
              <div className="temp-row" key={i}>
                <select
                  className="temp-label"
                  value={override.ingredient_id}
                  onChange={(e) => updateOverride(i, 'ingredient_id', e.target.value)}
                >
                  <option value="">Choose an ingredient…</option>
                  {overridable.map((line) => (
                    <option key={line.ingredient_id} value={line.ingredient_id}>
                      {line.ingredient_name} (base {formatPct(line.percentage)})
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  step="any"
                  min="0"
                  className="temp-value"
                  value={override.pct}
                  onChange={(e) => updateOverride(i, 'pct', e.target.value)}
                  placeholder="2.0"
                />
                <span className="temp-dash">%</span>
                {overrides.length > 1 && (
                  <button
                    type="button"
                    className="yield-remove"
                    onClick={() => setOverrides((rows) => rows.filter((_, idx) => idx !== i))}
                    aria-label="Remove override"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="yield-add" onClick={() => setOverrides((r) => [...r, blankOverride()])}>
              + Add another line
            </button>
          </div>

          {/* The resolution, shown while typing: this is the number that
              decides whether the batch lands on 100%. */}
          {preview.error ? (
            <p className="formula-error">{preview.error.message}</p>
          ) : (
            flexLine && (
              <div className="variant-preview">
                <span>{flexLine.ingredient_name} absorbs the difference</span>
                <strong>
                  {formatPct(flexLine.percentage)} → {formatPct(resolvedFlex?.percentage)}
                </strong>
                <em>Total {formatPct(preview.total)}</em>
              </div>
            )
          )}

          <label className="batch-notes-field">
            Notes
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="IFRA cap 2.5% for this FO — do not exceed"
            />
          </label>

          {error && <p className="formula-error">{error}</p>}

          <div className="ingredient-form-actions">
            <button type="button" className="ghost-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-action" disabled={saving || Boolean(preview.error)}>
              {saving ? 'Saving…' : editing ? 'Save variant' : 'Add variant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
